import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Container, LoadingOverlay, Stack, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import Draft, { convertFromHTML } from "draft-js";
import { useEffect, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/@custom/Input";
import { SaveTermsAuthorizationArgs, saveTermsAuthorizationArgsSchema, useAuth, useGetTermsUserByCompanyQuery, useSaveTermsAuthorizationMutation } from "@/store/apis/nextcargo";
import { useGetDriverQuery } from "@/store/apis/nextcargo/driver";

export const Route = createFileRoute("/aprovar-termo/empresa/$companyId/$driverId")({
  component: AprovarTermos
});

type Schema = z.infer<typeof schema>;
const schema = saveTermsAuthorizationArgsSchema.extend({
  fone: z
    .string()
    .regex(/\(\d{2}\) \d \d{4}-\d{4}/, "Formato de telefone inválido!")
    .optional(),
  email: z.string().email("Email inválido!").optional()
});

function AprovarTermos() {
  const EditorState = Draft.EditorState;
  const ContentState = Draft.ContentState;
  const [editorState, setEditorState] = useState(() => EditorState.createWithContent(ContentState.createFromText("")));

  const { companyId } = Route.useParams();
  const driverStorageId = sessionStorage.getItem("@App:temporaryDriverId");

  const { SignOut } = useAuth();
  const { driverId } = Route.useParams();

  const { data: driver, isLoading: isDriveLoading } = useGetDriverQuery({ id: Number(driverStorageId)! }, { skip: !driverStorageId });
  const { data: userTerms, isLoading: isUserTermsLoading } = useGetTermsUserByCompanyQuery({ id: Number(companyId)! }, { skip: !companyId });
  const [trigger] = useSaveTermsAuthorizationMutation();

  const {
    formState: { isSubmitting },
    handleSubmit: handleSubmitV2,
    control,
    reset
  } = useForm({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      hasEmail: false,
      hasSMS: false,
      hasWhats: false,

      fone: "",
      email: "",
      contact: []
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { hasEmail, hasSMS, hasWhats, useTerm, contact, email, fone } = data;

    const sharedBody = {
      hasWhats,
      hasSMS,
      hasEmail,
      useTerm
    };

    const driverBody = {
      ...sharedBody,
      driver: {
        id: Number(driverId)
      },
      contact: (contact || []).map((c, i) =>
        i === 0
          ? {
              ...c,
              id: fone || email ? 0 : c.id,
              email: email ?? c.email ?? "",
              fone: fone ?? c.fone ?? ""
            }
          : {
              ...c,
              fone: c.fone ?? "",
              email: c.email ?? ""
            }
      )
    };

    const userBody = {
      ...sharedBody,
      user: {
        id: Number(driverId)
      },
      contact: []
    };

    const body = (driverStorageId ? driverBody : userBody) as SaveTermsAuthorizationArgs;
    await trigger(body);
  };

  const handleSignOut = () => {
    if (!driverStorageId) return SignOut("expedition");
    SignOut("driver");
  };

  useEffect(() => {
    if (driver) {
      reset((values) => ({
        ...values,
        fone: driver.contact?.[0]?.fone ?? "",
        email: driver.contact?.[0]?.email ?? "",
        contact: (driver.contact || []).map((item) => ({
          ...item,
          fone: item.fone ?? "",
          email: item.email ?? "",
          whatsapp: typeof item.whatsapp === "boolean" ? item.whatsapp : false
        }))
      }));
    }

    if (userTerms) {
      reset((values) => ({
        ...values,
        useTerm: {
          id: userTerms.id,
          updatedAt: userTerms.updatedAt || ""
        }
      }));
    }
  }, [driver, userTerms]);

  useEffect(() => {
    if (userTerms) {
      const blocksFromHTML = convertFromHTML(userTerms.content);
      const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      setEditorState(EditorState.createWithContent(state));
    }
  }, [userTerms]);

  if (isDriveLoading || isUserTermsLoading) return <LoadingOverlay visible loaderProps={{ size: "xl", type: "bars" }} transitionProps={{ transition: "fade", duration: 500 }} />;

  return (
    <Container>
      <form onSubmit={handleSubmitV2(onSubmit)} autoComplete="off">
        <Stack>
          <Title order={1} className="max-md:text-center">
            Política de Privacidade e Termos de Uso
          </Title>

          <Stack>
            <Text ta="right">
              Versão: <strong>{dayjs(userTerms?.updatedAt).format("DD/MM/YYYY")}</strong>
            </Text>

            <Card shadow="md" mah={400} className="overflow-auto!" withBorder>
              <Editor readOnly={false} editorState={editorState} toolbarClassName="hidden" />
            </Card>

            <Stack gap="xs">
              <Text size="lg" fw="bold" lh="normal" className="max-md:text-center">
                Confirme suas informações de contato
              </Text>

              <Stack gap="xs">
                {driverStorageId && (
                  <Input.Text label="Telefone Celular" maskProps={{ mask: "(99) 9 9999-9999" }} placeholder="Telefone" name="fone" variant="default" control={control} />
                )}

                <Input.Checkbox label="Autorizar mensagens ao whatsapp" name="hasWhats" control={control} />

                <Input.Checkbox label="Autorizar envio de mensagens sms" name="hasSMS" control={control} />
              </Stack>

              <Stack gap="xs">
                {driverStorageId && <Input.Text label="Email" placeholder="E-mail" name="email" variant="default" control={control} />}

                <Input.Checkbox label="Autorizar envio de mensagens ao email" name="hasEmail" control={control} />
              </Stack>
            </Stack>

            <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              <Button color="red" onClick={handleSignOut} disabled={isSubmitting}>
                Não aceito
              </Button>

              <Button color="green" type="submit" loading={isSubmitting}>
                Eu aceito os termos acima
              </Button>
            </div>
          </Stack>
        </Stack>
      </form>
    </Container>
  );
}
